import * as React from "react";

import {
  ActionIcon,
  Badge,
  Divider,
  Flex,
  Grid,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { UseCaseBasicType } from "@supportinitiative/common";
import { getUseCaseDashboardData } from "../../api";
import { UserContext } from "../../misc/UserContext";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import DLinks from "./DLinks";
import DBadge from "./DBadge";

function Field({
  entityId,
  useCaseId,
  runId,
  runSeq,
  access,
  data,
  component,
}: {
  entityId: string;
  useCaseId: string;
  runId: string;
  runSeq: string;
  access: string;
  data: any;
  component?: string;
}) {
  if (data) {
    if (data.component === "BADGE") {
      return <DBadge data={data} component={component} />;
    } else if (data.component === "LINKS") {
      return (
        <DLinks
          entityId={entityId}
          useCaseId={useCaseId}
          runId={runId}
          runSeq={runSeq}
          access={access}
          data={data}
          component={component}
        />
      );
    } else {
      return <Text size="xs">{data.value1.value}</Text>;
    }
  } else {
    return <Text size="xs">-</Text>;
  }
}

export default function Dashboard({
  entityId,
  useCaseId,
  runId,
  runSeq,
  access,
  dashboard,
}: {
  entityId: string;
  useCaseId: string;
  runId: string;
  runSeq: string;
  access: string;
  dashboard: UseCaseBasicType["definition"]["dashboards"][number];
}) {
  const theme = useMantineTheme();

  const { state: userState, dispatch: userStateDispatch } =
    React.useContext(UserContext);

  const [fetching, setFetching] = React.useState(false);

  const [minId, setMinId] = React.useState<number | null>(null);
  const [maxId, setMaxId] = React.useState<number | null>(null);
  const [currentMinId, setCurrentMinId] = React.useState<number | null>(null);
  const [currentMaxId, setCurrentMaxId] = React.useState<number | null>(null);

  const [data, setData] = React.useState<{ id: string }[]>([]);

  React.useEffect(() => {
    setFetching(true);
    /*     setMinId(null);
    setMaxId(null);
    setCurrentMinId(null);
    setCurrentMaxId(null); */
    const getData = async () => {
      let values = await getUseCaseDashboardData(
        entityId,
        useCaseId,
        runId,
        runSeq,
        access,
        dashboard.id,
        20,
        "next",
        null,
        null,
        null,
        null
      );
      setData(values.data);
      if (values.data && values.data.length > 0) {
        setCurrentMinId(values.data[0].id);
        setCurrentMaxId(values.data[values.data.length - 1].id);
      }
      setMinId(values.minId);
      setMaxId(values.maxId);
      setFetching(false);
    };
    getData();
  }, [entityId, useCaseId, runId, runSeq, access]);

  // console.log("data", data.length, minId, maxId, currentMinId, currentMaxId);

  return (
    <Stack mt="xs" h="100%" p="xs" bg="#FFFBDE">
      <Flex align={"end"}>
        <Title
          mt="xs"
          order={5}
          py={0}
          px={theme.spacing.sm}
          style={{
            borderLeft: `4px solid ${theme.colors.emerald[0]}`,
          }}
        >
          {dashboard.name}
        </Title>

        <Tooltip label="Previous">
          <ActionIcon
            size="sm"
            variant="transparent"
            color="gray"
            ml="xs"
            onClick={async () => {
              setFetching(true);
              const values = await getUseCaseDashboardData(
                entityId,
                useCaseId,
                runId,
                runSeq,
                access,
                dashboard.id,
                20,
                "prev",
                currentMinId,
                currentMaxId,
                minId,
                maxId
              );
              setData(values.data);
              if (values.data && values.data.length > 0) {
                setCurrentMinId(values.data[0].id);
                setCurrentMaxId(values.data[values.data.length - 1].id);
              }
              setMinId(values.minId);
              setMaxId(values.maxId);
              setFetching(false);
            }}
            disabled={currentMinId === minId || fetching}
          >
            <IconChevronLeft
              style={{ width: "70%", height: "70%" }}
              stroke={2}
            />
          </ActionIcon>
        </Tooltip>

        <Tooltip label="Next">
          <ActionIcon
            size="sm"
            variant="transparent"
            color="gray"
            ml="xs"
            onClick={async () => {
              setFetching(true);
              const values = await getUseCaseDashboardData(
                entityId,
                useCaseId,
                runId,
                runSeq,
                access,
                dashboard.id,
                20,
                "next",
                currentMinId,
                currentMaxId,
                minId,
                maxId
              );
              setData(values.data);
              if (values.data && values.data.length > 0) {
                setCurrentMinId(values.data[0].id);
                setCurrentMaxId(values.data[values.data.length - 1].id);
              }
              setMinId(values.minId);
              setMaxId(values.maxId);
              setFetching(false);
            }}
            disabled={currentMaxId === maxId || fetching}
          >
            <IconChevronRight
              style={{ width: "70%", height: "70%" }}
              stroke={2}
            />
          </ActionIcon>
        </Tooltip>
      </Flex>

      <ScrollArea style={{ height: 250 }} offsetScrollbars scrollbarSize={16}>
        <Table
          verticalSpacing="xs"
          fs="xs"
          horizontalSpacing={"xs"}
          style={{ marginBottom: theme.spacing.xs }}
        >
          <Table.Thead>
            <Table.Tr>
              {dashboard.displayColumns.map((col) => {
                return (
                  <Table.Th
                    key={col.name}
                    style={{
                      minWidth: `${col.label.length * 7}px`,
                    }}
                  >
                    {col.label}
                  </Table.Th>
                );
              })}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(data || []).map((rowData: any, index: number) => {
              return (
                <Table.Tr key={index}>
                  {dashboard.displayColumns.map((col, index) => {
                    return (
                      <Table.Td key={col.name}>
                        <Field
                          entityId={entityId}
                          useCaseId={useCaseId}
                          runId={runId}
                          runSeq={runSeq}
                          access={access}
                          data={rowData[`field_${index + 1}`]}
                          component={col.component}
                        />
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Stack>
  );
}
